/**
 * The composer's microphone (Figma 05 · Voice): record, watch the level, stop
 * or cancel. It only records; the rail has it written down and keeps the words.
 * Levels come from an AnalyserNode, sampled every LEVEL_MS, for the live bars.
 */
import { useCallback, useEffect, useRef, useState } from "react";

/** One level sample per this many ms: the live waveform's step. */
const LEVEL_MS = 80;
/** The live waveform shows this many of the newest samples. */
export const LIVE_BARS = 64;
/** A note this long stops by itself. */
const MAX_MS = 10 * 60 * 1000;

/** What the browser records, best first, as the backend accepts it (attachmentTypes.ts). */
const FORMATS: Array<{ mime: string; type: string; extension: string }> = [
  { mime: "audio/webm;codecs=opus", type: "audio/webm", extension: "webm" },
  { mime: "audio/webm", type: "audio/webm", extension: "webm" },
  { mime: "audio/mp4", type: "audio/mp4", extension: "m4a" },
  { mime: "audio/ogg;codecs=opus", type: "audio/ogg", extension: "ogg" },
];


export function voiceSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof MediaRecorder !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    FORMATS.some((f) => MediaRecorder.isTypeSupported(f.mime))
  );
}

export function useVoiceRecorder() {
  const [recording, setRecording] = useState(false);
  const [levels, setLevels] = useState<number[]>([]);
  const [elapsedMs, setElapsedMs] = useState(0);

  const session = useRef<{
    recorder: MediaRecorder;
    stream: MediaStream;
    context: AudioContext;
    timer: number;
    chunks: Blob[];
    startedAt: number;
    format: (typeof FORMATS)[number];
    stopped: Promise<void>;
  } | null>(null);
  const autoStop = useRef<(() => void) | null>(null);

  const teardown = useCallback(() => {
    const s = session.current;
    if (!s) return;
    session.current = null;
    window.clearInterval(s.timer);
    s.stream.getTracks().forEach((track) => track.stop());
    void s.context.close().catch(() => {});
    setRecording(false);
    setLevels([]);
    setElapsedMs(0);
  }, []);

  useEffect(() => teardown, [teardown]);

  /** Asks for the microphone; throws when it is refused or missing. */
  const start = useCallback(async () => {
    if (session.current) return;
    const format = FORMATS.find((f) => MediaRecorder.isTypeSupported(f.mime));
    if (!format) throw new Error("This browser cannot record.");
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const context = new AudioContext();
    const analyser = context.createAnalyser();
    analyser.fftSize = 1024;
    context.createMediaStreamSource(stream).connect(analyser);
    const buffer = new Uint8Array(analyser.fftSize);

    const recorder = new MediaRecorder(stream, { mimeType: format.mime });
    const chunks: Blob[] = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    const stopped = new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
    });
    recorder.start(250);

    const recent: number[] = [];
    const startedAt = performance.now();
    const timer = window.setInterval(() => {
      analyser.getByteTimeDomainData(buffer);
      let sum = 0;
      for (const v of buffer) sum += ((v - 128) / 128) ** 2;
      // RMS of speech sits well under 0.3; scale it to fill the bar.
      const level = Math.min(1, Math.sqrt(sum / buffer.length) * 4);
      recent.push(level);
      if (recent.length > LIVE_BARS) recent.shift();
      const now = performance.now() - startedAt;
      setLevels([...recent]);
      setElapsedMs(now);
      if (now >= MAX_MS) autoStop.current?.();
    }, LEVEL_MS);

    session.current = { recorder, stream, context, timer, chunks, startedAt, format, stopped };
    setRecording(true);
  }, []);

  /** Ends the recording and hands over the file; null when nothing was recording. */
  const stop = useCallback(async (): Promise<File | null> => {
    const s = session.current;
    if (!s) return null;
    if (s.recorder.state !== "inactive") s.recorder.stop();
    await s.stopped;
    const blob = new Blob(s.chunks, { type: s.format.type });
    const { format } = s;
    teardown();
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    return new File([blob], `voice-${stamp}.${format.extension}`, { type: format.type });
  }, [teardown]);

  /** Throws the note away. */
  const cancel = useCallback(() => {
    const s = session.current;
    if (!s) return;
    s.recorder.ondataavailable = null;
    if (s.recorder.state !== "inactive") s.recorder.stop();
    teardown();
  }, [teardown]);

  return { recording, levels, elapsedMs, start, stop, cancel, autoStop };
}
